import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./inboxTabScroller.css";

type InboxTabScrollerProps = {
  children: React.ReactNode;
};

const VISIBLE_EDGE_GAP = 16;

const InboxTabScroller = ({ children }: InboxTabScrollerProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const measure = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const hasOverflow = maxScroll > 1;
    setOverflow(hasOverflow);
    setCanLeft(hasOverflow && el.scrollLeft > 1);
    setCanRight(hasOverflow && el.scrollLeft < maxScroll - 1);
  }, []);

  const scrollActiveIntoView = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const checked = el.querySelector("input:checked");
    const label = checked?.closest("label");
    if (!(label instanceof HTMLElement)) return;
    const labelRect = label.getBoundingClientRect();
    const viewRect = el.getBoundingClientRect();
    if (labelRect.left < viewRect.left) {
      el.scrollLeft -= viewRect.left - labelRect.left + 8;
    } else if (labelRect.right > viewRect.right) {
      el.scrollLeft += labelRect.right - viewRect.right + 8;
    }
  }, []);

  // The inbox table can be wider than the screen, which stretches the list
  // toolbar past the visible edge and pushes the actions (+ New) off-screen.
  const fitToolbarToScreen = useCallback(() => {
    const toolbar = rootRef.current?.parentElement;
    if (!toolbar) return;
    toolbar.classList.add("inbox-tab-toolbar");
    const content = toolbar.closest(".RaLayout-content");
    const visibleRight = Math.min(
      window.innerWidth,
      content ? content.getBoundingClientRect().right : window.innerWidth
    );
    // Leave the current maxWidth in place while measuring. Clearing it first
    // widens the toolbar, the ResizeObserver fires, and the cap is applied
    // again, so a wide inbox table never settles.
    const available = Math.floor(
      visibleRight - toolbar.getBoundingClientRect().left - VISIBLE_EDGE_GAP
    );
    if (available <= 0) return;
    const next = `${available}px`;
    if (toolbar.style.maxWidth !== next) {
      toolbar.style.maxWidth = next;
    }
  }, []);

  useLayoutEffect(() => {
    const toolbar = rootRef.current?.parentElement;
    if (!toolbar) return;
    toolbar.classList.add("inbox-tab-toolbar");
    return () => toolbar.classList.remove("inbox-tab-toolbar");
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const toolbar = rootRef.current?.parentElement;

    const refreshWithSelection = () => {
      fitToolbarToScreen();
      scrollActiveIntoView();
      measure();
    };

    refreshWithSelection();
    // Viewport size changes when the window or the sidebar changes.
    // Count and search updates do not resize this element, so the observer
    // stays on the viewport. Watching the tab row retriggers the width cap.
    const observer = new ResizeObserver(refreshWithSelection);
    observer.observe(el);
    const onScroll = () => measure();
    const onClick = () => {
      requestAnimationFrame(refreshWithSelection);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("click", onClick);
    window.addEventListener("resize", refreshWithSelection);

    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("click", onClick);
      window.removeEventListener("resize", refreshWithSelection);
      if (toolbar) toolbar.style.maxWidth = "";
    };
  }, [measure, scrollActiveIntoView, fitToolbarToScreen]);

  useEffect(() => {
    fitToolbarToScreen();
    measure();
  }, [children, fitToolbarToScreen, measure]);

  const scrollByDirection = (direction: number) => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(140, Math.floor(el.clientWidth * 0.75)),
      behavior: "smooth",
    });
  };

  return (
    <div className="inbox-tab-scroller" ref={rootRef}>
      {overflow && (
        <button
          type="button"
          className="inbox-tab-scroller__arrow"
          aria-label="Scroll tabs left"
          disabled={!canLeft}
          onClick={() => scrollByDirection(-1)}
        >
          <ChevronLeftIcon fontSize="small" />
        </button>
      )}
      <div className="inbox-tab-scroller__viewport" ref={viewportRef}>
        {children}
      </div>
      {overflow && (
        <button
          type="button"
          className="inbox-tab-scroller__arrow"
          aria-label="Scroll tabs right"
          disabled={!canRight}
          onClick={() => scrollByDirection(1)}
        >
          <ChevronRightIcon fontSize="small" />
        </button>
      )}
    </div>
  );
};

export default InboxTabScroller;
